import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

/**
 * Simple Login page for demo/testing.
 * - Hardcoded demo accounts:
 *    HR:    hr@example.com / password
 *    Emp:   emp@example.com / password
 *    Mgr:   mgr@example.com / password
 *
 * On success, stores localStorage.user = JSON.stringify({ email, role, name })
 * and redirects to:
 *   /hr/dashboard
 *   /employee/dashboard
 *   /manager/dashboard
 *
 * Replace the auth logic with your API call in production.
 */

const demoUsers = {
  "hr@example.com": { password: "password", role: "hr", name: "HR Admin" },
  "emp@example.com": { password: "password", role: "employee", name: "John Doe" },
  "mgr@example.com": { password: "password", role: "manager", name: "Jane Manager" },
};

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleHint, setRoleHint] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function handleDemo(role) {
    // Fill demo credentials for role
    const emailMap = {
      hr: "hr@example.com",
      employee: "emp@example.com",
      manager: "mgr@example.com",
    };
    const demoEmail = emailMap[role];
    setEmail(demoEmail);
    setPassword("password");
    setRoleHint(`Using demo account for ${role.toUpperCase()}`);
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Simulate network delay
    await new Promise((r) => setTimeout(r, 400));

    const user = demoUsers[email?.toLowerCase()];
    if (!user || user.password !== password) {
      setError("Invalid credentials. Use demo accounts or register.");
      setLoading(false);
      return;
    }

    // Create minimal user object for the app
    const userObj = {
      email,
      name: user.name,
      role: user.role,
      token: "demo-token", // replace with real token from API
    };

    // Persist (AuthContext would normally manage this)
    try {
      localStorage.setItem("user", JSON.stringify(userObj));
    } catch (err) {
      console.warn("localStorage set error", err);
    }

    // Redirect depending on role
    setLoading(false);
    if (user.role === "hr") navigate("/hr/dashboard");
    else if (user.role === "manager") navigate("/manager/dashboard");
    else navigate("/employee/dashboard");
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 flex items-center justify-center p-6">
      <div className="w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
        {/* Left — Branding / Illustration */}
        <div className="hidden md:flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-indigo-600 to-violet-500 text-white p-10">
          <div className="text-4xl font-extrabold tracking-tight">HR Portal</div>
          <div className="text-sm opacity-90 max-w-xs">
            A modern HR system — role-based access. This demo uses Tailwind CSS and React Router.
          </div>

          <div className="w-full max-w-sm mt-4">
            <div className="grid grid-cols-1 gap-2">
              <button
                onClick={() => handleDemo("hr")}
                className="w-full rounded-md py-2 font-medium bg-white bg-opacity-20 hover:bg-opacity-30 transition"
              >
                Demo: HR (Full access)
              </button>
              <button
                onClick={() => handleDemo("manager")}
                className="w-full rounded-md py-2 font-medium bg-white bg-opacity-20 hover:bg-opacity-30 transition"
              >
                Demo: Manager (Team-level)
              </button>
              <button
                onClick={() => handleDemo("employee")}
                className="w-full rounded-md py-2 font-medium bg-white bg-opacity-20 hover:bg-opacity-30 transition"
              >
                Demo: Employee (Self only)
              </button>
            </div>
          </div>

          <div className="mt-6 text-xs opacity-80">
            Demo credentials: <br />
            <span className="font-medium">password</span> for all accounts
          </div>
        </div>

        {/* Right — Login form */}
        <div className="bg-white p-8 md:p-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold">Sign in</h1>
              <p className="text-sm text-gray-500 mt-1">
                Welcome back — sign in to continue to your dashboard
              </p>
            </div>
            <div className="text-xs text-gray-400">v1.0</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {roleHint && <div className="text-sm text-indigo-600">{roleHint}</div>}

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@company.com"
                required
                className="mt-1 block w-full rounded-lg border-gray-200 shadow-sm focus:ring-2 focus:ring-indigo-300 p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                placeholder="Enter password"
                required
                className="mt-1 block w-full rounded-lg border-gray-200 shadow-sm focus:ring-2 focus:ring-indigo-300 p-2"
              />
            </div>

            {error && <div className="text-sm text-red-600">{error}</div>}

            <div className="flex items-center justify-between gap-4">
              <button
                type="submit"
                className="flex-1 inline-flex items-center justify-center rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 font-medium transition disabled:opacity-60"
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
              <Link
                to="/auth/forgot-password"
                className="text-sm text-indigo-600 hover:underline"
              >
                Forgot?
              </Link>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm text-gray-500">
                New to portal?{" "}
                <Link to="/auth/register" className="text-indigo-600 hover:underline">
                  Create an account
                </Link>
              </p>
            </div>
          </form>

          {/* Small footer / hints */}
          <div className="mt-6 text-xs text-gray-400">
            Tip: Click the demo buttons (on the left) or use the demo emails shown in the left panel.
          </div>

          {/* Mobile demo buttons when left panel hidden */}
          <div className="mt-6 md:hidden">
            <div className="flex gap-2">
              <button
                onClick={() => handleDemo("hr")}
                className="flex-1 rounded-md py-2 text-sm border border-gray-200"
              >
                HR demo
              </button>
              <button
                onClick={() => handleDemo("employee")}
                className="flex-1 rounded-md py-2 text-sm border border-gray-200"
              >
                Emp demo
              </button>
              <button
                onClick={() => handleDemo("manager")}
                className="flex-1 rounded-md py-2 text-sm border border-gray-200"
              >
                Mgr demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
